import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { MemberNameLink } from '@/components/shared/member-name-link';
import { Pagination } from '@/components/shared/pagination';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { formatDateTime } from '@/utils/format';

interface GroupJoinRequestRow {
  id: string;
  memberId: string;
  memberName: string;
  status: string;
  createdAt: string;
}

interface GroupJoinRequestsPanelProps {
  groupId: string;
  canManage: boolean;
  category: 'committee' | 'group';
}

export const GroupJoinRequestsPanel = ({
  groupId,
  canManage,
  category,
}: GroupJoinRequestsPanelProps) => {
  const { t } = useTranslation('groups');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [declineTarget, setDeclineTarget] = useState<GroupJoinRequestRow | null>(null);
  const [decidingId, setDecidingId] = useState<string | null>(null);

  const entity = category === 'committee' ? 'committee' : 'group';

  const joinRequests = useQuery({
    queryKey: ['groups', groupId, 'join-requests', page],
    queryFn: () =>
      api.get<{
        data: GroupJoinRequestRow[];
        page: { page: number; pageSize: number; totalItems: number; totalPages: number };
      }>(`/groups/${groupId}/join-requests?page=${page}&pageSize=25`),
    enabled: Boolean(groupId) && canManage,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['groups', groupId, 'join-requests'] });
    void queryClient.invalidateQueries({ queryKey: ['groups', groupId, 'insights'] });
    void queryClient.invalidateQueries({ queryKey: ['groups', groupId] });
    void queryClient.invalidateQueries({ queryKey: ['groups'] });
    if (category === 'committee') {
      void queryClient.invalidateQueries({ queryKey: ['committees'] });
      void queryClient.invalidateQueries({ queryKey: ['committees', 'insights'] });
    }
  };

  const decide = useMutation({
    mutationFn: ({ requestId, approve }: { requestId: string; approve: boolean }) =>
      api.post(`/groups/join-requests/${requestId}/decide`, { approve }),
    onMutate: ({ requestId }) => {
      setDecidingId(requestId);
    },
    onSuccess: (_result, { approve }) => {
      toast.success(
        approve
          ? t('joinRequests.approved', { entity })
          : t('joinRequests.declined'),
      );
      setDeclineTarget(null);
      invalidate();
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
    onSettled: () => {
      setDecidingId(null);
    },
  });

  const columns: DataTableColumn<GroupJoinRequestRow>[] = [
    {
      id: 'requestedAt',
      header: t('joinRequests.columns.requestedAt'),
      cell: (row) => formatDateTime(row.createdAt),
    },
    {
      id: 'name',
      header: t('joinRequests.columns.name'),
      cell: (row) => <MemberNameLink memberId={row.memberId} name={row.memberName} />,
    },
    ...(canManage
      ? [
          {
            id: 'actions',
            header: '',
            cell: (row: GroupJoinRequestRow) => (
              <div className="flex justify-end gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={decide.isPending}
                  onClick={(event) => {
                    event.stopPropagation();
                    setDeclineTarget(row);
                  }}
                >
                  {t('joinRequests.decline')}
                </Button>
                <Button
                  size="sm"
                  loading={decidingId === row.id && decide.isPending}
                  disabled={decide.isPending}
                  onClick={(event) => {
                    event.stopPropagation();
                    decide.mutate({ requestId: row.id, approve: true });
                  }}
                >
                  {t('joinRequests.approve')}
                </Button>
              </div>
            ),
          } satisfies DataTableColumn<GroupJoinRequestRow>,
        ]
      : []),
  ];

  const rows = joinRequests.data?.data ?? [];
  const pageMeta = joinRequests.data?.page;

  return (
    <>
      <DataTable
        rows={rows}
        columns={columns}
        rowKey={(row) => row.id}
        loading={joinRequests.isLoading}
        empty={
          <div className="flex flex-col items-start gap-1">
            <p className="text-body-sm font-medium text-fg-primary">{t('joinRequests.empty')}</p>
            <p className="text-body-sm text-fg-secondary">
              {t('joinRequests.emptyDescription', { entity })}
            </p>
          </div>
        }
      />

      {pageMeta && pageMeta.totalPages > 1 ? (
        <Pagination
          className="mt-4"
          page={pageMeta.page}
          pageSize={pageMeta.pageSize}
          totalItems={pageMeta.totalItems}
          totalPages={pageMeta.totalPages}
          onPageChange={setPage}
          labels={{
            showing: ({ from, to, total }) =>
              tCommon('pagination.showing', { from, to, total, defaultValue: '{{from}}–{{to}} of {{total}}' }),
            perPage: tCommon('pagination.perPage', { defaultValue: 'Per page' }),
            first: tCommon('pagination.first', { defaultValue: 'First' }),
            previous: tCommon('pagination.previous', { defaultValue: 'Previous' }),
            next: tCommon('pagination.next', { defaultValue: 'Next' }),
            last: tCommon('pagination.last', { defaultValue: 'Last' }),
          }}
        />
      ) : null}

      <ConfirmDialog
        open={declineTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeclineTarget(null);
        }}
        title={t('joinRequests.declineConfirmTitle')}
        description={t('joinRequests.declineConfirmDescription', {
          name: declineTarget?.memberName ?? '',
          entity,
        })}
        confirmLabel={t('joinRequests.decline')}
        cancelLabel={tCommon('action.cancel')}
        destructive
        loading={decide.isPending}
        onConfirm={() => {
          if (!declineTarget) return;
          decide.mutate({ requestId: declineTarget.id, approve: false });
        }}
      />
    </>
  );
};
